import React from "react";
import { Box, Table, Text, Tag } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import TicketSessionByServiceLineGraph from "../charts/TicketSessionByServiceLineGraph";
import ReportDataTable from "../../reports/ReportDataTable";
import {
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
} from "../../../store/renderConfig";
import { formatTime, formatNumber } from "../../../store/helpers";
import { getServiceTimePerServiceRowsForScope } from "../../../store/serviceTimePerServiceExport";

const sectionNumber = (...parts) => `${parts.filter(Boolean).join(".")}.`;

export const renderServiceTimePerServiceCell = (serviceData, columnId) => {
  if (columnId === "serviceName") return serviceData.serviceName || serviceData.name;
  if (columnId === "completed") return <Text color="var(--blue)">{formatNumber(serviceData.completed)}</Text>;
  if (columnId === "totalServiceTime") return <Text color="var(--blue)">{formatTime(serviceData.sessionInSeconds)}</Text>;
  if (columnId === "average") return formatTime(serviceData.avg);
  if (columnId === "min") return formatTime(serviceData.min);
  if (columnId === "max") return formatTime(serviceData.max);
  return null;
};

const ServiceTimePerServiceTable = ({
  branchId = null,
  data,
  branchIndex,
  numServices,
  branchName,
  renderConfig,
  onRenderConfigChange,
}) => {
  const { showEmptyDataSets } = useSelector((state) => state.defaultReducer);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const chartTitle = branchId ? `${branchName} Branch, ` : "Entire Organization,";
  const branchTitle = branchId
    ? `${sectionNumber(branchIndex)} ${branchName} Branch`
    : "Entire Organization";
  const scope = branchId ? "Branch" : "Organization";
  const rows = getServiceTimePerServiceRowsForScope({ data, branchName, scope }, config);
  const visibleChartRows = (data || []).filter(
    (item) => showEmptyDataSets || (!showEmptyDataSets && Number(item.completed) > 0)
  );

  return (
    <Box mt={10}>
      {!branchId ? (
        <Box mb="20px">
          <h2>{sectionNumber(1)} Entire Organization</h2>
        </Box>
      ) : null}

      {!config.charts.enabled ? (
        <Box className="serviceTimePerServiceBlock">
          {branchId ? (
            <Box className="branchServiceHeader" mb="10px">
              <h2>{branchTitle}</h2>
              <p>{numServices} services.</p>
            </Box>
          ) : null}
          <ReportDataTable
            columns={SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS}
            rows={rows}
            renderConfig={config}
            renderCell={renderServiceTimePerServiceCell}
            presortedRows={true}
            onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
          />
        </Box>
      ) : null}

      {config.charts.enabled
        ? visibleChartRows.map((serviceData, index) => {
            const serviceTitle = `${sectionNumber(branchIndex, index + 1)} ${serviceData.name}`;
            return (
              <Box
                key={index}
                className={`reportContainer serviceTimePerServiceBlock${
                  branchId && index === 0 ? " branchServiceStart" : ""
                }`}
                mt="20px"
              >
                {branchId && index === 0 ? (
                  <Box className="branchServiceHeader" mb="10px">
                    <h2>{branchTitle}</h2>
                    <p>{numServices} services.</p>
                    <h3>{serviceTitle}</h3>
                    <Tag.Root>
                      <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                    </Tag.Root>
                  </Box>
                ) : (
                  <Box mb="10px">
                    <h3>{serviceTitle}</h3>
                    {branchId ? (
                      <Tag.Root>
                        <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                      </Tag.Root>
                    ) : null}
                  </Box>
                )}

                <Table.Root className="stripedTable" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader borderRight="1px solid #dadce0">Total Completed Tickets</Table.ColumnHeader>
                      <Table.ColumnHeader borderRight="1px solid #dadce0">Total Service Time</Table.ColumnHeader>
                      <Table.ColumnHeader>Avg /ticket.</Table.ColumnHeader>
                      <Table.ColumnHeader>Min.</Table.ColumnHeader>
                      <Table.ColumnHeader>Max</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell borderRight="1px solid #dadce0">{renderServiceTimePerServiceCell(serviceData, "completed")}</Table.Cell>
                      <Table.Cell borderRight="1px solid #dadce0">{renderServiceTimePerServiceCell(serviceData, "totalServiceTime")}</Table.Cell>
                      <Table.Cell>{renderServiceTimePerServiceCell(serviceData, "average")}</Table.Cell>
                      <Table.Cell>{renderServiceTimePerServiceCell(serviceData, "min")}</Table.Cell>
                      <Table.Cell>{renderServiceTimePerServiceCell(serviceData, "max")}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell colSpan={5} p={0}>
                        <Box mt="20px" mb="20px">
                          <TicketSessionByServiceLineGraph
                            title={`${serviceData.name}: ${chartTitle} Day by Day Comparison`}
                            chart={serviceData.days}
                            chartHeight="200px"
                          />
                        </Box>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
              </Box>
            );
          })
        : null}
      {!rows.length && !config.charts.enabled ? (
        <Text fontSize="sm">No data available for this report</Text>
      ) : null}
    </Box>
  );
};

export default ServiceTimePerServiceTable;
